/**
 * HTTP-backed implementation of `WorkingCopyPort` (TG-4, m1-exposure-detail-
 * sourcing), over the published portal API routes this feature's own TG-3
 * republished at contract 2.0.0: `GET/PUT /api/portal/working-copy/sections`
 * and `.../drafts`, keyed by section INSTANCE id (ADR-20261019-1ery,
 * ADR-20261019-m4zr as amended).
 *
 * This file is the adapter between the wire shape (a
 * `WorkingCopySectionRecord` — `sectionInstanceId`, `answers`,
 * `writtenByRole`, `createdAtUtc`, `updatedAtUtc`) and `WorkingCopyPort`'s
 * own, UNCHANGED TypeScript contract (`ConfirmedSection` — `sectionId`,
 * `answers`). It does not rename or widen that TypeScript interface:
 * every existing caller across this feature (`ExposuresJourneyRoute.tsx`,
 * `confirm.ts`, `working-copy-client.ts`, `MultiSectionStepRoute.tsx`,
 * `ConfirmDetailsRoute.tsx` and more) already passes a `SectionInstanceId`
 * STRING as that port's `sectionId` parameter — `SectionInstanceId` is a
 * string superset of `SectionId` (routing/route-table.ts), so the value
 * these callers already supply is, byte for byte, the section instance id
 * this route's `{sectionInstanceId}` path segment names. Only this
 * adapter needs to know the wire field is called something else; renaming
 * the port's own `sectionId` parameter across dozens of already-shipped
 * call sites is a different, larger change this task group's own files
 * list does not include.
 *
 * The vendored contract (`../contracts/working-copy.contract.json`,
 * 2.0.0) is therefore NOT a mirror of this TypeScript interface — it is a
 * faithful copy of the real, published `working-copy.openapi.yaml`
 * (`conformance.test.ts` diffs the two directly). The two shapes
 * genuinely differ (the wire record also carries `writtenByRole`,
 * `createdAtUtc`, `updatedAtUtc`; this port's `ConfirmedSection` does
 * not) — that is this adapter's whole reason to exist, not a conformance
 * gap.
 *
 * ADR-20260821-kp45 / FIND-35/OBJ-20: a dropped network connection on a
 * WRITE is this feature's canonical Indeterminate trigger. `writeSection`'s
 * own callers are already wrapped by `working-copy-client.ts`'s
 * `saveSection`, which re-maps any thrown/rejected outcome to
 * Indeterminate regardless — but `writeDraftSection` has no such outer
 * wrapper (`working-copy-port.ts`'s own doc comment: a caller "guards each
 * call with `port.writeDraftSection?.(...)`" directly), so BOTH write
 * methods below map their own network-level failure to Indeterminate
 * themselves, rather than relying on a caller that may not exist.
 *
 * ADR-20261018-1skz: every operation is gated by `whenSessionTokenHeld` —
 * no request fires while no session token is held.
 */
import { authenticatedFetch } from "../../portal-auth/authenticated-fetch";
import type { ReadOutcome, WriteOutcome } from "../outcome";
import type {
  ConfirmedSection,
  DraftSection,
  WorkingCopyDraftReadResult,
  WorkingCopyDraftWriteResult,
  WorkingCopyPort,
  WorkingCopyReadResult,
  WorkingCopyWriteResult,
} from "../working-copy-port";
import { whenSessionTokenHeld } from "./session-gate";

const SECTIONS_ROUTE = "/api/portal/working-copy/sections";
const DRAFTS_ROUTE = "/api/portal/working-copy/drafts";

interface WireSectionRecord {
  sectionInstanceId: string;
  answers: Record<string, unknown>;
}

function toConfirmedSection(record: WireSectionRecord): ConfirmedSection {
  return { sectionId: record.sectionInstanceId, answers: record.answers };
}

function toDraftSection(record: WireSectionRecord): DraftSection {
  return { sectionId: record.sectionInstanceId, answers: record.answers };
}

function readFailed<T>(httpStatus: number, message: string): ReadOutcome<T> {
  return { status: "Failed", detail: { httpStatus, message } };
}

/** m1-portal-ux-fixes (Fix D): `taxonomyCode`, when given, carries the
 * response body's own `code` field (e.g. `"not_writable"`, 409 — a renewal
 * already submitted) straight through to `OutcomeFailureDetail`, so a
 * caller can recognise it via `isRenewalSubmittedWriteRefusal` without this
 * adapter needing to know what any particular code means. */
function writeFailed<T>(httpStatus: number, message: string, taxonomyCode?: string): WriteOutcome<T> {
  return { status: "Failed", detail: { httpStatus, message, ...(taxonomyCode !== undefined ? { taxonomyCode } : {}) } };
}

/** The HTTP statuses `taxonomy-to-http.json` assigns to the Indeterminate
 * outcomes (`Unknown` 500, `TransportTimeout` 504) -- a disjoint set from
 * every Failed status. */
const INDETERMINATE_WRITE_STATUSES: ReadonlySet<number> = new Set([500, 504]);

function writeIndeterminate<T>(message: string, httpStatus = 0, taxonomyCode?: string): WriteOutcome<T> {
  return {
    status: "Indeterminate",
    detail: { httpStatus, message, ...(taxonomyCode !== undefined ? { taxonomyCode } : {}) },
  };
}

async function readSectionList(route: string): Promise<ReadOutcome<readonly WireSectionRecord[]>> {
  let response: Response;
  try {
    response = await authenticatedFetch(route, { method: "GET" });
  } catch (error) {
    return readFailed(0, error instanceof Error ? error.message : String(error));
  }
  if (response.status !== 200) {
    return readFailed(response.status, `GET ${route} returned ${response.status}.`);
  }
  try {
    const body = (await response.json()) as { sections?: WireSectionRecord[] };
    if (body.sections === undefined) {
      throw new Error(`GET ${route}: 200 response carried no readable "sections" field.`);
    }
    return { status: "Succeeded", value: body.sections };
  } catch (error) {
    return readFailed(response.status, error instanceof Error ? error.message : String(error));
  }
}

async function writeSectionRecord(
  route: string,
  sectionInstanceId: string,
  answers: Record<string, unknown>,
): Promise<WriteOutcome<{ sectionInstanceId: string }>> {
  let response: Response;
  try {
    response = await authenticatedFetch(`${route}/${encodeURIComponent(sectionInstanceId)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(answers),
    });
  } catch (error) {
    // A dropped network connection -- did the server apply this write or
    // not? Unknown (ADR-20260821-kp45) -- never Failed.
    return writeIndeterminate(error instanceof Error ? error.message : String(error));
  }

  if (response.status !== 200) {
    // Fix D: parse the body's own `code` (e.g. 409 `not_writable`) so a
    // caller can route a submitted renewal to `/renewal-status` instead of
    // rendering it as an ordinary save failure. A body that doesn't parse
    // (or carries no string `code`) is treated exactly as before — a plain
    // Failed outcome with no taxonomy code, never a thrown error.
    let taxonomyCode: string | undefined;
    try {
      const body = (await response.json()) as { code?: unknown };
      taxonomyCode = typeof body.code === "string" ? body.code : undefined;
    } catch {
      taxonomyCode = undefined;
    }
    const message = `PUT ${route}/${sectionInstanceId} returned ${response.status}.`;
    if (INDETERMINATE_WRITE_STATUSES.has(response.status)) {
      // Unknown (500) and TransportTimeout (504) are the taxonomy's
      // Indeterminate statuses (taxonomy-to-http.json, ADR-20260821-kp45): the
      // server may have applied the write before it failed or timed out.
      return writeIndeterminate(message, response.status, taxonomyCode);
    }
    return writeFailed(response.status, message, taxonomyCode);
  }

  try {
    const body = (await response.json()) as { sectionInstanceId?: string };
    return { status: "Succeeded", value: { sectionInstanceId: body.sectionInstanceId ?? sectionInstanceId } };
  } catch (error) {
    // The write itself may well have landed (200 was already returned) --
    // a response body we cannot parse is not evidence it did not. Unknown,
    // not Failed.
    return writeIndeterminate(error instanceof Error ? error.message : String(error));
  }
}

async function performReadConfirmedSections(): Promise<ReadOutcome<WorkingCopyReadResult>> {
  const outcome = await readSectionList(SECTIONS_ROUTE);
  if (outcome.status === "Failed") {
    return outcome;
  }
  return { status: "Succeeded", value: { renewalId: "", confirmedSections: outcome.value.map(toConfirmedSection) } };
}

async function performReadDraftSections(): Promise<ReadOutcome<WorkingCopyDraftReadResult>> {
  const outcome = await readSectionList(DRAFTS_ROUTE);
  if (outcome.status === "Failed") {
    return outcome;
  }
  return { status: "Succeeded", value: { renewalId: "", draftSections: outcome.value.map(toDraftSection) } };
}

export function createHttpWorkingCopyPort(): WorkingCopyPort & { isSampleData: false } {
  return {
    isSampleData: false,

    async readConfirmedSections(renewalId): Promise<ReadOutcome<WorkingCopyReadResult>> {
      const outcome = await whenSessionTokenHeld(performReadConfirmedSections);
      if (outcome.status === "Failed") {
        return outcome;
      }
      return { status: "Succeeded", value: { ...outcome.value, renewalId } };
    },

    async writeSection(renewalId, sectionId, answers): Promise<WriteOutcome<WorkingCopyWriteResult>> {
      const outcome = await whenSessionTokenHeld(() => writeSectionRecord(SECTIONS_ROUTE, sectionId, answers));
      if (outcome.status !== "Succeeded") {
        return outcome;
      }
      return {
        status: "Succeeded",
        value: { renewalId, sectionId: outcome.value.sectionInstanceId, savedAt: new Date().toISOString() },
      };
    },

    async readDraftSections(renewalId): Promise<ReadOutcome<WorkingCopyDraftReadResult>> {
      const outcome = await whenSessionTokenHeld(performReadDraftSections);
      if (outcome.status === "Failed") {
        return outcome;
      }
      return { status: "Succeeded", value: { ...outcome.value, renewalId } };
    },

    async writeDraftSection(renewalId, sectionId, answers): Promise<WriteOutcome<WorkingCopyDraftWriteResult>> {
      const outcome = await whenSessionTokenHeld(() => writeSectionRecord(DRAFTS_ROUTE, sectionId, answers));
      if (outcome.status !== "Succeeded") {
        return outcome;
      }
      return {
        status: "Succeeded",
        value: { renewalId, sectionId: outcome.value.sectionInstanceId, savedAt: new Date().toISOString() },
      };
    },
  };
}
